import { Box, Flex, Spinner, Text, VStack, Skeleton, SkeletonText } from '@chakra-ui/react'
import { PRIMARY_COLOR } from '../config/theme'

/**
 * PageLoader - Full page loading spinner for Suspense fallbacks
 * Now with a more subtle, faster appearance
 */
export function PageLoader({ message }) {
  return (
    <Flex
      minH="60vh"
      align="center"
      justify="center"
      w="full"
      opacity={0}
      animation="fadeIn 0.3s ease-in 0.15s forwards"
      sx={{
        '@keyframes fadeIn': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      <VStack spacing={4}>
        <Spinner
          thickness="3px"
          speed="0.6s"
          emptyColor="gray.200"
          color={PRIMARY_COLOR}
          size="lg"
        />
        {message && (
          <Text color="gray.400" fontSize="sm">
            {message}
          </Text>
        )}
      </VStack>
    </Flex>
  )
}

/**
 * PageSkeleton - Skeleton placeholder for page content
 * Shows immediately, no delay - better perceived performance
 */
export function PageSkeleton() {
  return (
    <Box w="full" maxW="1200px" mx="auto" px={4} py={8}>
      <Skeleton height="40px" width="60%" mb={6} />
      <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="3" />
      <Flex gap={4} mt={8}>
        <Skeleton height="200px" flex={1} borderRadius="lg" />
        <Skeleton height="200px" flex={1} borderRadius="lg" display={{ base: 'none', md: 'block' }} />
        <Skeleton height="200px" flex={1} borderRadius="lg" display={{ base: 'none', lg: 'block' }} />
      </Flex>
    </Box>
  )
}

/**
 * SectionLoader - Smaller loading spinner for component sections
 */
export function SectionLoader({ size = "lg", message }) {
  return (
    <Flex
      py={8}
      align="center"
      justify="center"
      w="full"
    >
      <VStack spacing={3}>
        <Spinner
          thickness="3px"
          speed="0.65s"
          emptyColor="gray.200"
          color={PRIMARY_COLOR}
          size={size}
        />
        {message && (
          <Text color="gray.500" fontSize="xs">
            {message}
          </Text>
        )}
      </VStack>
    </Flex>
  )
}

/**
 * SkeletonCard - Placeholder card while content loads
 */
export function SkeletonCard() {
  return (
    <Box
      bg="gray.100"
      borderRadius="lg"
      h="200px"
      w="full"
      animation="pulse 2s infinite"
      sx={{
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
      }}
    />
  )
}

export default PageLoader
